import type { ItineraryItem } from './itinerary';

export const tripPlaceTypes = ['lodging', 'activity', 'convenience'] as const;
export type TripPlaceType = (typeof tripPlaceTypes)[number];

/** A photo uploaded to Immich and attached to a trip place. The actual image
 *  bytes live in the user's self-hosted Immich instance — we only persist the
 *  asset ID and lightweight metadata, and fetch thumbnails/originals through
 *  the worker's /api/immich/* proxy at render time. */
export interface ImmichPhotoRef {
  /** Immich asset ID — opaque UUID. */
  assetId: string;
  /** Original filename the photo was uploaded with. */
  originalFileName?: string;
  /** ISO 8601 timestamp when the photo was taken (from EXIF if available,
   *  otherwise upload time). */
  fileCreatedAt?: string;
  width?: number;
  height?: number;
  /** Local timestamp of the upload, used for sort fallback when EXIF is absent. */
  uploadedAt?: number;
}

export interface TripPlace {
  addedAt: number;
  /** Last-write-wins clock for live sync. Bumped on every mutation that
   *  touches this place's fields. Compared against the same field on an
   *  incoming snapshot's same-id place — whichever has the higher value
   *  wins. Legacy places without this field get backfilled from
   *  `addedAt` on load (see backfillTrip); fixtures and freshly-built
   *  literals can omit it (merge code treats undefined as 0). */
  updatedAt?: number;
  item: ItineraryItem;
  placeType: TripPlaceType;
  scheduledDay?: number | null; // 1-indexed day within the trip
  scheduledDayEnd?: number | null; // inclusive last day — only meaningful for lodging spanning multiple nights
  scheduledTime?: string | null; // "HH:MM" 24h — only meaningful for activities
  /** User-set duration for this place. Independent of the item's data-source
   * estimatedDuration range — captures the user's plan, not the venue's hint. */
  estimatedDurationMinutes?: number | null;
  /** Lodging-only: time range for check-in on `scheduledDay`. Both fields are
   * "HH:MM" 24h. Lodgings with a `checkInTime` are surfaced in the timeline
   * for the check-in day at that start time. */
  checkInTime?: string | null;
  checkInEndTime?: string | null;
  /** Lodging-only: the check-out deadline on `scheduledDayEnd`. Unlike
   *  check-in this is a single time, not a window. */
  checkOutTime?: string | null;
  /** RETIRED 2026-08-02 — check-out is a deadline you have to be out by, not a
   *  window, so the second field made no sense. No longer written (saves force
   *  null) or displayed. Kept only so values stored before the change still
   *  round-trip through sync instead of tripping the op whitelist. Safe to drop
   *  with a migration once none remain. Not marked `@deprecated`: every
   *  surviving reference is deliberate plumbing, so the tag would only produce
   *  warnings on code that has to keep working. */
  checkOutEndTime?: string | null;
  note?: string | null;
  /** Photos attached to this place. All photos for a trip live in the same
   *  Immich album (see `Trip.immichAlbumId`); per-place attachment is tracked
   *  only here in the trip JSON. */
  immichPhotos?: Array<ImmichPhotoRef>;
}

export const enRouteDirections = ['in', 'out'] as const;
export type EnRouteDirection = (typeof enRouteDirections)[number];

export interface EnRouteNote {
  id: string;
  text: string;
  /** 'in' = on the way to the trip (Day 1), 'out' = on the way home (last day). Defaults to 'in' for legacy notes. */
  direction?: EnRouteDirection;
  /** Last-write-wins clock for live sync. Bumped on text edits.
   *  Legacy notes without this field are backfilled to the trip's
   *  `createdAt` so any fresh edit naturally wins. */
  updatedAt?: number;
}

export interface Trip {
  id: string;
  name: string;
  location: string; // display name of the search location when the trip was created
  lat?: number | null;
  lng?: number | null;
  /** Search radius (miles) captured when the trip was created, restored when
   *  the trip is selected. Persisted so the trip's places search reuses the
   *  same (center, radius) — and therefore the same worker cache entry — that
   *  warmed the area during exploration. Optional: legacy trips predate it and
   *  fall back to the UI default on select. */
  radius?: number | null;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  createdAt: number;
  /** Last-write-wins clock for trip-level scalar fields (name, dates,
   *  location, lat/lng, enRoute, immichPhotos, collaborators). Bumped on
   *  every mutation. Place-level mutations also bump this — it's the
   *  trip-wide "something changed" pulse used to decide which side's
   *  scalar fields win in the merge. Optional so fixtures and legacy
   *  data can omit it; merge code treats undefined as 0. */
  updatedAt?: number;
  /** Tombstones for places deleted on this device. Keyed by place id
   *  (`TripPlace.item.id`), value is the epoch-ms delete timestamp.
   *  Merged into peers' copies so deletes propagate without depending
   *  on the absence of a place (otherwise B's late add of X would
   *  resurrect after A's delete of X). A tombstone overrides a
   *  surviving same-id place only when its timestamp is later than the
   *  place's `updatedAt`; otherwise re-edit wins over delete. Tombstones
   *  older than 30 days are garbage-collected during merge. */
  deletedPlaceIds?: Record<string, number>;
  /** Tombstones for en-route notes deleted on this device. Keyed by
   *  note id, value epoch-ms. Same merge semantics as `deletedPlaceIds`. */
  deletedEnRouteNoteIds?: Record<string, number>;
  /** Tombstones for Immich photos removed on this device. Keyed by
   *  `assetId` (globally unique within a trip — assetIds come from the
   *  same Immich album), value epoch-ms. Applies to BOTH trip-level
   *  photos and per-place photos; the merge filters either array
   *  against this single tombstone map. */
  deletedPhotoAssetIds?: Record<string, number>;
  places: Array<TripPlace>;
  enRoute?: Array<EnRouteNote>;
  /** Immich album holding every photo attached to this trip. Created lazily
   *  on the first photo upload (not when the trip itself is created). Album
   *  title mirrors the trip name; description carries the trip location and
   *  the URL the user uploaded from. */
  immichAlbumId?: string;
  /** Trip-level photos — attached to the trip overall rather than to a
   *  specific place. Aggregated alongside `TripPlace.immichPhotos` in
   *  the trip-header photo strip. Stored in the same Immich album as
   *  place photos (one album per trip). */
  immichPhotos?: Array<ImmichPhotoRef>;
  /** Collaborators granted editor access to the trip's Immich photo
   *  album. Emails are validated against the configured Immich instance
   *  at add-time (rejected upfront if no matching user) and persisted
   *  with the resolved `immichUserId` so subsequent revokes don't need
   *  a fresh lookup. Album-level Immich sharing is the only effect of
   *  this list — collaborators don't get any in-app Trippy permissions. */
  collaborators?: Array<TripCollaborator>;
}

export interface TripCollaborator {
  email: string;
  /** Resolved Immich user ID — set only when the email matches an Immich
   *  account, so their photos sync via the trip's Immich album. Trip
   *  view/edit access is keyed on `email` (see `authorizeTrip`), so this is
   *  optional: a collaborator without Immich can still edit the trip. */
  immichUserId?: string;
  /** Local timestamp when the collaborator was added — used for stable
   *  display ordering. */
  addedAt: number;
}
